import { useEffect, useState, useRef, useCallback } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Edit, Trash2, RefreshCw, Eye, Monitor, ExternalLink, Lock } from "lucide-react";
import { SearchBar } from "@/components/ui/search-bar";
import { Button } from "@/components/ui/button";
import { supabase } from '@/lib/supabase';
import PromoModal from './PromoModal';
import { useScreens } from '@/hooks/useScreens';
import { useMedias } from '@/hooks/useMedias';
import { useAuth } from '@/hooks/useAuth';
import { useUserPlan } from '@/hooks/useUserPlan';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from '@/hooks/use-toast';
import { Toggle } from '@/components/ui/toggle';

// Tipagem baseada em src/lib/types.ts
interface Promo {
  id: string;
  title: string;
  nome?: string;
  objetivo?: string;
  url: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  duration: number;
  screen_ids: string[];
  active: boolean;
  interval: number;
  Typepromo?: string;
  Solicitar?: string;
  'Modal visível'?: 'HIDDEN' | 'TEMPORARY' | 'PERMANENT' | 'LINKED';
  campaign_id?: string;
  selected_media_ids?: string[] | null;
}

const PromoSection = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const impersonatedUserId = params.get('impersonate') || undefined;
  const isImpersonating = !!impersonatedUserId && !!user?.email && user.email.toLowerCase().includes('admin');
  const effectiveUserId = isImpersonating ? impersonatedUserId : (user?.id || '');
  const { hasActivePlan, loading: planLoading } = useUserPlan(effectiveUserId);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const { screens } = useScreens(effectiveUserId);
  const { medias } = useMedias(effectiveUserId);
  const [editPromo, setEditPromo] = useState<Promo | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreatePromoModal, setShowCreatePromoModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showScreensPopup, setShowScreensPopup] = useState<string | null>(null);

  // Busca promoções do Supabase
  const fetchPromos = useCallback(async () => {
    if (!effectiveUserId) {
      console.log('[PROMO] Usuário não disponível ainda, aguardando...');
      return;
    }

    setLoading(true);
    console.log('[PROMO] Buscando promoções do banco para usuário:', effectiveUserId);
    const { data, error } = await supabase
      .from('promos')
      .select('*, Solicitar, "Modal visível", campaign_id, selected_media_ids')
      .eq('user_id', effectiveUserId)
      .order('created_at', { ascending: false });

    console.log('[PROMO] Query de busca - error:', error);
    console.log('[PROMO] Query de busca - data sample:', data?.[0]);

    if (error) {
      console.error('[PROMO] Erro ao buscar promoções:', error);
      setLoading(false);
      return;
    }

    if (data) {
      console.log('[PROMO] Promoções recebidas:', data);
      console.log('[PROMO] Número de promoções:', data.length);
      console.log('[PROMO] Primeira promoção com Solicitar:', data[0]?.Solicitar);
      console.log('[PROMO] Primeira promoção Modal visível:', data[0]?.['Modal visível']);
      console.log('[PROMO] Primeira promoção campaign_id:', data[0]?.campaign_id);
      console.log('[PROMO] Primeira promoção selected_media_ids:', data[0]?.selected_media_ids);
      setPromos(data as Promo[]);
    } else {
      console.log('[PROMO] Nenhuma promoção encontrada');
      setPromos([]);
    }
    setLoading(false);
  }, [effectiveUserId]);

  useEffect(() => {
    if (effectiveUserId) {
      fetchPromos();
      // Assinatura realtime
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
      const subscription = supabase
        .channel('promos-realtime')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'promos',
        }, (payload) => {
          console.log('[PROMO] Evento realtime recebido:', payload);
          fetchPromos();
        })
        .subscribe();
      subscriptionRef.current = subscription;
      return () => {
        if (subscriptionRef.current) {
          supabase.removeChannel(subscriptionRef.current);
          subscriptionRef.current = null;
        }
      };
    }
  }, [effectiveUserId, fetchPromos]);

  // Filtro de busca por nome da campanha (considera nome e título)
  const filteredPromos = promos.filter(promo => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    const name = (promo.nome || promo.title || '').toLowerCase();
    return name.includes(query);
  });


  // Excluir promoção
  const handleDelete = async (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    await supabase.from('promos').delete().eq('id', deleteConfirmId);
    setDeleteConfirmId(null);
    fetchPromos();
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };

  // Alternar status ativo/inativo da promoção
  const togglePromoActive = async (promo: Promo) => {
    setLoading(true);
    const newStatus = !promo.active;
    if (newStatus) {
      const parseDateTime = (d?: unknown, t?: unknown) => {
        const ds = String(d || '').trim();
        let ts = String(t || '').trim();
        if (ts && ts.length === 5) ts = `${ts}:00`;
        const iso = ds && ts ? `${ds}T${ts}` : `${ds} ${ts}`;
        return new Date(iso);
      };
      const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) => aStart <= bEnd && bStart <= aEnd;
      const screenNameById = new Map((screens || []).map(s => [s.id, s.name]));
      const { data: existing } = await supabase
        .from('promos')
        .select('id, active, start_date, start_time, end_date, end_time, screen_ids')
        .eq('user_id', effectiveUserId);
      const targetStart = parseDateTime(promo.start_date, promo.start_time);
      const targetEnd = parseDateTime(promo.end_date, promo.end_time);
      // Removido: não bloquear por conflito de período
    }
    const { error } = await supabase
      .from('promos')
      .update({ active: newStatus })
      .eq('id', promo.id);
    setLoading(false);
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar status',
        description: error.message
      });
    } else {
      toast({
        title: newStatus ? 'Promoção ativada' : 'Promoção inativada',
        description: `Promoção "${promo.title}" agora está ${newStatus ? 'ativa' : 'inativa'}.`
      });
      fetchPromos();
      window.dispatchEvent(new CustomEvent('promotionUpdated', { detail: { promoId: promo.id } }));
    }
  };

  // Função para obter os nomes das telas selecionadas
  const getSelectedScreenNames = (screenIds: string[]) => {
    return screenIds
      .map(id => screens.find(screen => screen.id === id)?.name)
      .filter(Boolean) as string[];
  };

  // Função para obter os nomes das mídias selecionadas
  const getSelectedMediaNames = (mediaIds: string[] | null) => {
    if (!mediaIds) return 'Todas as mídias';
    if (mediaIds.length === 0) return 'Nenhuma mídia';

    const mediaNames = mediaIds
      .map(id => medias.find(media => media.id === id)?.title)
      .filter(Boolean) as string[];

    return mediaNames.length > 0 ? mediaNames.join(', ') : 'Mídias não encontradas';
  };

  // Função para garantir que a URL tenha protocolo
  const ensureProtocol = (url: string) => {
    if (!url) return url;

    // Se já tem protocolo, retorna como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Se não tem www, adiciona
    if (!url.startsWith('www.')) {
      return `https://www.${url}`;
    }

    // Se tem www mas não tem protocolo, adiciona https://
    return `https://${url}`;
  };

  // Função para encurtar URL para exibição
  const shortenUrl = (url: string, maxLength: number = 40) => {
    if (!url) return '';

    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      const path = urlObj.pathname + urlObj.search + urlObj.hash;

      // Se a URL completa é menor que o limite, retorna completa
      if (url.length <= maxLength) {
        return url;
      }

      // Se apenas o domínio é maior que o limite, encurta o domínio
      if (domain.length > maxLength - 10) {
        return `${domain.substring(0, maxLength - 10)}...`;
      }

      // Encurta o path mantendo o domínio
      const availableLength = maxLength - domain.length - 3; // 3 para "..."
      if (path.length > availableLength) {
        return `${domain}${path.substring(0, availableLength)}...`;
      }

      return url;
    } catch {
      // Se não for uma URL válida, encurta simplesmente
      return url.length > maxLength ? `${url.substring(0, maxLength)}...` : url;
    }
  };

  // Função para atualizar promoção
  const handleUpdatePromo = async (updatedPromo: Record<string, unknown>) => {
    setLoading(true);
    const parseDateTime = (d?: unknown, t?: unknown) => {
      const ds = String(d || '').trim();
      let ts = String(t || '').trim();
      if (ts && ts.length === 5) ts = `${ts}:00`;
      const iso = ds && ts ? `${ds}T${ts}` : `${ds} ${ts}`;
      const dt = new Date(iso);
      return dt;
    };
    const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) => aStart <= bEnd && bStart <= aEnd;
    const screenNameById = new Map((screens || []).map(s => [s.id, s.name]));
    const { data: existingAll } = await supabase
      .from('promos')
      .select('id, nome, title, start_date, start_time, end_date, end_time, active, screen_ids')
      .eq('user_id', effectiveUserId);
    const targetStart = parseDateTime(updatedPromo.start_date || updatedPromo.startDate, updatedPromo.start_time || updatedPromo.startTime);
    const targetEnd = parseDateTime(updatedPromo.end_date || updatedPromo.endDate, updatedPromo.end_time || updatedPromo.endTime);
    const targetIds = Array.isArray(updatedPromo.selectedScreens)
      ? (updatedPromo.selectedScreens as string[]).map(String)
      : Array.isArray(updatedPromo.screen_ids)
        ? (updatedPromo.screen_ids as string[]).map(String)
        : [];
    const targetSet = new Set(targetIds.map(String));
    // Sem validação de conflito de período na edição
    const payload = {
      title: updatedPromo.quickPhrase || updatedPromo.title || updatedPromo.name,
      nome: updatedPromo.name || updatedPromo.nome,
      objetivo: updatedPromo.goal || updatedPromo.objetivo,
      url: updatedPromo.url,
      start_date: updatedPromo.startDate || updatedPromo.start_date,
      end_date: updatedPromo.endDate || updatedPromo.end_date,
      start_time: updatedPromo.startTime || updatedPromo.start_time,
      end_time: updatedPromo.endTime || updatedPromo.end_time,
      duration: Number(updatedPromo.duration) || 30,
      screen_ids: Array.isArray(updatedPromo.selectedScreens)
        ? updatedPromo.selectedScreens.map(String)
        : (Array.isArray(updatedPromo.screen_ids) ? updatedPromo.screen_ids.map(String) : []),
      interval: Number(updatedPromo.interval) || 30,
      Typepromo: updatedPromo.Typepromo || 'QR code',
      Solicitar: updatedPromo.Solicitar || '',
      'Modal visível': updatedPromo['Modal visível'] || 'TEMPORARY',
      campaign_id: updatedPromo.campaign_id || null,
      selected_media_ids: updatedPromo.selected_media_ids || null,
      active: true,
      user_id: effectiveUserId // Usa effectiveUserId para suportar modo impersonate
    };
    console.log("Payload enviado para o Supabase (UPDATE):", payload);
    console.log("ID da promoção a ser atualizada:", updatedPromo.id);
    console.log("=== CAMPAIGN_ID DEBUG ===");
    console.log("updatedPromo.campaign_id recebido:", updatedPromo.campaign_id);
    console.log("campaign_id no payload:", payload.campaign_id);
    console.log("Tipo do campaign_id:", typeof updatedPromo.campaign_id);

    // Primeiro, vamos verificar se a promoção existe
    const { data: existingPromo, error: checkError } = await supabase
      .from('promos')
      .select('*')
      .eq('id', updatedPromo.id)
      .single();

    if (checkError) {
      console.error("Erro ao verificar promoção existente:", checkError);
      toast({
        variant: "destructive",
        title: "Erro de Verificação",
        description: checkError.message
      });
      return;
      setLoading(false);
      return;
    }

    console.log("Promoção existente encontrada:", existingPromo);

    // Agora vamos tentar atualizar
    const { error, data, count } = await supabase
      .from('promos')
      .update(payload)
      .eq('id', updatedPromo.id)
      .select();

    console.log("Resultado da atualização:", { error, data, count });
    console.log("=== ERROR DEBUG ===");
    if (error) {
      console.error("Detalhes do erro:", error);
      console.error("Mensagem do erro:", error.message);
      console.error("Código do erro:", error.code);
      console.error("Detalhes completos:", JSON.stringify(error, null, 2));
    }

    setLoading(false);

    if (error) {
      console.error("Erro ao atualizar promoção:", error);
      alert('Erro ao atualizar promoção: ' + error.message);
    } else {
      console.log("Promoção atualizada com sucesso:", data);
      setShowEditModal(false);
      setEditPromo(null);
      await fetchPromos();
      toast({
        title: "✅ Promoção Atualizada",
        description: "As alterações foram salvas com sucesso."
      });

      // Dispara o evento para notificar o player sobre a atualização
      console.log(`🚀 Disparando evento 'promotionUpdated' para a promoção ID: ${updatedPromo.id}`);
      window.dispatchEvent(new CustomEvent('promotionUpdated', {
        detail: { promoId: updatedPromo.id }
      }));
    }
  };

  const handleCreatePromo = async (promoData: Record<string, unknown>) => {
    setLoading(true);
    const parseDateTime = (d?: unknown, t?: unknown) => {
      const ds = String(d || '').trim();
      let ts = String(t || '').trim();
      if (ts && ts.length === 5) ts = `${ts}:00`;
      const iso = ds && ts ? `${ds}T${ts}` : `${ds} ${ts}`;
      return new Date(iso);
    };
    const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) => aStart <= bEnd && bStart <= aEnd;
    const screenNameById = new Map((screens || []).map(s => [s.id, s.name]));
    const { data: existingAll } = await supabase
      .from('promos')
      .select('id, nome, title, start_date, start_time, end_date, end_time, active, screen_ids')
      .eq('user_id', effectiveUserId);
    const targetStart = parseDateTime(promoData.start_date || promoData.startDate, promoData.start_time || promoData.startTime);
    const targetEnd = parseDateTime(promoData.end_date || promoData.endDate, promoData.end_time || promoData.endTime);
    const targetIds = Array.isArray(promoData.selectedScreens) ? (promoData.selectedScreens as string[]).map(String) : [];
    const payload = {
      title: promoData.quickPhrase,
      nome: promoData.name,
      objetivo: promoData.goal,
      url: promoData.url,
      start_date: promoData.startDate,
      end_date: promoData.endDate,
      start_time: promoData.startTime,
      end_time: promoData.endTime,
      duration: promoData.duration,
      screen_ids: Array.isArray(promoData.selectedScreens) ? promoData.selectedScreens.map(String) : [],
      interval: promoData.interval,
      Typepromo: promoData.Typepromo,
      Solicitar: promoData.Solicitar,
      'Modal visível': promoData['Modal visível'],
      selected_media_ids: promoData.selected_media_ids || null,
      active: true,
      user_id: effectiveUserId // Usa effectiveUserId para suportar modo impersonate
    };
    console.log("Payload enviado para o Supabase (CREATE):", payload);
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('"QT_cupom_User"')
        .eq('id', effectiveUserId)
        .single();

      if ((profile?.QT_cupom_User ?? 0) <= 0) {
        setLoading(false);
        toast({
          variant: 'destructive',
          title: 'Limite de promoções atingido',
          description: 'Você já utilizou todas as promoções do seu plano. Faça upgrade para liberar mais.'
        });
        return;
      }

      const { data, error } = await supabase.rpc('create_promo_with_quota', {
        p_user_id: effectiveUserId,
        p_promo: payload as any
      });

      setLoading(false);

      if (error) {
        const exceeded = String(error.message || '').includes('quota_exceeded');
        toast({
          variant: exceeded ? 'destructive' : 'default',
          title: exceeded ? 'Limite de promoções atingido' : 'Erro ao salvar',
          description: exceeded ? 'Você atingiu o limite de promoções do seu plano. Faça upgrade para liberar mais.' : error.message
        });
        return;
      }

      await fetchPromos();

      const { data: profileAfter } = await supabase
        .from('profiles')
        .select('"QT_cupom_User"')
        .eq('id', effectiveUserId)
        .single();

      const remaining = profileAfter?.QT_cupom_User ?? null;
      toast({
        title: '✅ Promoção Criada',
        description: remaining != null ? `Saldo de promoções restante: ${remaining}` : 'Nova promoção foi criada com sucesso.'
      });
    } catch (err: any) {
      setLoading(false);
      toast({
        variant: 'destructive',
        title: 'Erro inesperado',
        description: err?.message || 'Falha ao criar promoção'
      });
      console.error('[PROMO] Erro inesperado na criação:', err);
    }
  };

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className="border rounded-lg bg-card shadow-sm mb-6">
      <CollapsibleTrigger
        className="flex items-center justify-between w-full p-3 sm:p-4 cursor-pointer"
        onClick={(e) => {
          if (isImpersonating) return;
          if (!hasActivePlan && !planLoading) {
            e.preventDefault();
            e.stopPropagation();
            window.dispatchEvent(new Event('openPlanModal'));
            toast({ title: 'Assinatura necessária', description: 'Ative um plano para acessar Campanhas.' });
          }
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
            Campanhas ({filteredPromos.length})
            {!isImpersonating && !hasActivePlan && !planLoading && <Lock className="h-4 w-4 text-muted-foreground" aria-label="Recurso bloqueado" />}
          </span>
          {loading && <RefreshCw className="animate-spin h-4 w-4 text-muted-foreground" />}
        </div>
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform text-muted-foreground [&[data-state=open]]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="p-2 sm:p-4 pt-0">
        <div className="mb-4 w-full flex justify-between items-center">
          <div className="w-40 sm:w-64 mr-2 sm:mr-4 md:mr-8">
            <SearchBar
              value={search}
              onSearch={setSearch}
              onClear={() => setSearch("")}
              placeholder="Buscar campanhas..."
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left font-semibold">Campanha</th>
                <th className="px-3 py-2 text-left font-semibold">URL/CTA</th>
                <th className="px-3 py-2 text-center font-semibold">Telas</th>
                <th className="px-3 py-2 text-center font-semibold">Mídias</th>
                <th className="px-3 py-2 text-center font-semibold">Período</th>
                <th className="px-3 py-2 text-center font-semibold">Duração</th>
                <th className="px-3 py-2 text-center font-semibold">Intervalo</th>
                <th className="px-3 py-2 text-center font-semibold">Status</th>
                <th className="px-3 py-2 text-center font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">Carregando campanhas...</td></tr>
              ) : filteredPromos.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">Nenhuma campanha encontrada.</td></tr>
              ) : filteredPromos.map(promo => (
                <tr key={promo.id} className="border-b hover:bg-gray-50 transition">
                  <td className="px-3 py-2 font-medium text-gray-800">{promo.nome || promo.title}</td>
                  <td className="px-3 py-2 text-center">
                    {promo.url ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        title={`Abrir: ${ensureProtocol(promo.url)}`}
                        onClick={() => window.open(ensureProtocol(promo.url), '_blank', 'noopener,noreferrer')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {promo.screen_ids.length} tela{promo.screen_ids.length !== 1 ? 's' : ''}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        title="Ver telas selecionadas"
                        onClick={() => setShowScreensPopup(promo.id)}
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="max-w-32">
                      {promo['Modal visível'] === 'TEMPORARY' && (
                        <Badge variant="default" className="text-xs bg-green-100 text-green-800 transition-none pointer-events-none select-none">
                          Todas
                        </Badge>
                      )}
                      {promo['Modal visível'] === 'LINKED' && (
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-200">
                          Específicas
                        </Badge>
                      )}
                      {promo['Modal visível'] === 'PERMANENT' && (
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-200">
                          Fixa
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">{promo.start_date} {promo.start_time} <br />até<br /> {promo.end_date} {promo.end_time}</td>
                  <td className="px-3 py-2 text-center">{promo.duration}s</td>
                  <td className="px-3 py-2 text-center">{promo.interval} min</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Toggle
                        checked={promo.active}
                        onChange={() => togglePromoActive(promo)}
                        disabled={loading}
                        size="sm"
                        aria-label={promo.active ? 'Inativar promoção' : 'Ativar promoção'}
                      />
                      <span className={promo.active ? "text-green-600 font-medium text-xs" : "text-gray-400 text-xs"}>
                        {promo.active ? "Ativa" : "Inativa"}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center flex gap-2 justify-center">
                    <Button variant="outline" size="icon" title="Editar" onClick={() => {
                      setEditPromo(promo);
                      setShowEditModal(true);
                    }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="destructive" size="icon" title="Excluir" onClick={() => handleDelete(promo.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
      {/* Popup de confirmação de deleção */}
      {deleteConfirmId && (
        <Dialog open={!!deleteConfirmId} onOpenChange={cancelDelete}>
          <DialogContent className="max-w-xs mx-auto p-6">
            <DialogHeader>
              <DialogTitle>Confirmar exclusão</DialogTitle>
            </DialogHeader>
            <div className="py-2 text-center text-base text-gray-700">
              Tem certeza que deseja deletar esta promoção?
            </div>
            <DialogFooter className="flex gap-2 justify-center mt-4">
              <Button variant="outline" onClick={cancelDelete}>Cancelar</Button>
              <Button variant="destructive" onClick={confirmDelete}>Deletar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Modal de criação de promoção */}
      {showCreatePromoModal && (
        <PromoModal
          open={showCreatePromoModal}
          onClose={() => setShowCreatePromoModal(false)}
          screens={screens}
          onSave={handleCreatePromo}
          userId={effectiveUserId}
        />
      )}
      {/* Modal de edição de promoção */}
      {showEditModal && editPromo && (
        <PromoModal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditPromo(null);
          }}
          screens={screens}
          initialData={{
            id: editPromo.id,
            nome: editPromo.nome || editPromo.title,
            objetivo: editPromo.objetivo || "",
            title: editPromo.title,
            quickPhrase: editPromo.title,
            url: editPromo.url,
            start_date: editPromo.start_date,
            end_date: editPromo.end_date,
            start_time: editPromo.start_time,
            end_time: editPromo.end_time,
            duration: editPromo.duration,
            interval: editPromo.interval,
            screen_ids: editPromo.screen_ids || [],
            selectedScreens: editPromo.screen_ids || [],
            Typepromo: editPromo.Typepromo || "QR code",
            Solicitar: editPromo.Solicitar || "",
            'Modal visível': editPromo['Modal visível'] || 'TEMPORARY',
            campaign_id: editPromo.campaign_id || "",
            selected_media_ids: editPromo.selected_media_ids || null
          }}
          onSave={handleUpdatePromo}
          userId={effectiveUserId}
        />
      )}
      {/* Popup de visualização das telas selecionadas */}
      {showScreensPopup && (
        <Dialog open={!!showScreensPopup} onOpenChange={() => setShowScreensPopup(null)}>
          <DialogContent className="max-w-md mx-auto p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-blue-600" />
                Telas Selecionadas
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {(() => {
                const promo = promos.find(p => p.id === showScreensPopup);
                if (!promo) return null;

                const selectedScreenNames = getSelectedScreenNames(promo.screen_ids);

                return (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 mb-3">
                      A promoção <strong>"{promo.nome || promo.title}"</strong> será exibida nas seguintes telas:
                    </p>
                    {selectedScreenNames.length > 0 ? (
                      <div className="grid gap-2">
                        {selectedScreenNames.map((screenName, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                            <Monitor className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-700">{screenName}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        <Monitor className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Nenhuma tela selecionada</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowScreensPopup(null)}
                className="w-full"
              >
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Collapsible>
  );
};

export default PromoSection;
